import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { updateProfile, addHighlight, updateHighlight, deleteHighlight } from '../../../store/slices/aboutSlice';
import Icon from '../../../components/AppIcon';
import HighlightForm from './HighlightForm';

const AboutAdmin = () => {
  const dispatch = useDispatch();
  const { profile, highlights } = useSelector(state => state.about);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState(profile);
  const [isHighlightFormOpen, setIsHighlightFormOpen] = useState(false);
  const [editingHighlight, setEditingHighlight] = useState(null);

  const handleProfileSave = () => {
    dispatch(updateProfile(profileData));
    setEditingProfile(false);
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDeleteHighlight = (id) => {
    if (window.confirm('Are you sure you want to delete this highlight?')) {
      dispatch(deleteHighlight(id));
    }
  };

  const handleAddHighlight = () => {
    setEditingHighlight(null);
    setIsHighlightFormOpen(true);
  };

  const handleEditHighlight = (highlight) => {
    setEditingHighlight(highlight);
    setIsHighlightFormOpen(true);
  };

  const handleSaveHighlight = (highlightData) => {
    if (editingHighlight) {
      dispatch(updateHighlight(highlightData));
    } else {
      dispatch(addHighlight(highlightData));
    }
    setIsHighlightFormOpen(false);
    setEditingHighlight(null);
  };

  const handleCloseHighlightForm = () => {
    setIsHighlightFormOpen(false);
    setEditingHighlight(null);
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-foreground">About Page Management</h2>
      </div>

      {/* Profile Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-foreground">Profile Information</h3>
          <button
            onClick={() => setEditingProfile(!editingProfile)}
            className="bg-primary text-primary-foreground px-4 py-2 rounded-md hover:bg-primary/90 transition-colors font-medium flex items-center"
          >
            <Icon name={editingProfile ? "X" : "Edit"} size={16} className="mr-2" />
            {editingProfile ? 'Cancel' : 'Edit Profile'}
          </button>
        </div>

        <div className="bg-surface rounded-lg p-6 security-shadow">
          {editingProfile ? (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Name</label>
                  <input
                    type="text"
                    name="name"
                    value={profileData.name}
                    onChange={handleProfileChange}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Title</label>
                  <input
                    type="text"
                    name="title"
                    value={profileData.title}
                    onChange={handleProfileChange}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Subtitle</label>
                <input
                  type="text"
                  name="subtitle"
                  value={profileData.subtitle}
                  onChange={handleProfileChange}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Description</label>
                <textarea
                  name="description"
                  value={profileData.description}
                  onChange={handleProfileChange}
                  rows={4}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Location</label>
                  <input
                    type="text"
                    name="location"
                    value={profileData.location}
                    onChange={handleProfileChange}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={profileData.email}
                    onChange={handleProfileChange}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={handleProfileSave}
                  className="bg-primary text-primary-foreground px-6 py-2 rounded-md hover:bg-primary/90 transition-colors font-medium"
                >
                  Save Changes
                </button>
                <button
                  onClick={() => setEditingProfile(false)}
                  className="bg-muted text-muted-foreground px-6 py-2 rounded-md hover:bg-muted/80 transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold text-foreground text-lg">{profile.name}</h4>
                <p className="text-primary font-medium">{profile.title}</p>
                <p className="text-text-secondary">{profile.subtitle}</p>
              </div>
              <p className="text-text-secondary">{profile.description}</p>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-foreground">Location:</span>
                  <span className="text-text-secondary ml-2">{profile.location}</span>
                </div>
                <div>
                  <span className="font-medium text-foreground">Email:</span>
                  <span className="text-text-secondary ml-2">{profile.email}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Highlights Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-foreground">Highlights</h3>
          <button 
            onClick={handleAddHighlight}
            className="bg-secondary text-secondary-foreground px-4 py-2 rounded-md hover:bg-secondary/90 transition-colors font-medium flex items-center"
          >
            <Icon name="Plus" size={16} className="mr-2" />
            Add Highlight
          </button>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {highlights.map((highlight) => (
            <div key={highlight.id} className="bg-surface rounded-lg p-4 security-shadow">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-semibold text-foreground">{highlight.title}</h4>
                  <p className="text-text-secondary text-sm">{highlight.description}</p>
                </div>
                <div className="flex space-x-1">
                  <button 
                    onClick={() => handleEditHighlight(highlight)}
                    className="p-1 text-text-secondary hover:text-primary transition-colors"
                  >
                    <Icon name="Edit" size={14} />
                  </button>
                  <button 
                    onClick={() => handleDeleteHighlight(highlight.id)}
                    className="p-1 text-text-secondary hover:text-destructive transition-colors"
                  >
                    <Icon name="Trash" size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Highlight Form Modal */}
      {isHighlightFormOpen && (
        <HighlightForm
          highlight={editingHighlight}
          onSave={handleSaveHighlight}
          onClose={handleCloseHighlightForm}
        />
      )}
    </div>
  );
};

export default AboutAdmin; 